import { useEffect, useMemo, useRef, useState } from "react";
import styled from "@emotion/styled";
import { CollapsibleContent } from "@radix-ui/react-collapsible";

import { BOOK_A_CALL_LINK } from "components/account_management/utils";
import Button, { CombinedButtonProps } from "components/ui/button/Button";
import {
  SidebarContent,
  SidebarGroup,
  SidebarMenu,
  SidebarWrapper,
} from "components/ui/sidebar/Sidebar";
import { RELEVANCY_SEARCH, RELEVANCY_SEARCH_NEW_USERS, useVariableValue } from "lib/featureFlags";
import { usePqlActioned } from "lib/hooks/api/users/usePqlActioned";
import { useSubscription } from "lib/hooks/subscription";
import { routes } from "lib/routes";
import { EventNames, useTracking } from "lib/tracking";
import { PqlActionType } from "lib/types/graphQLEnums";
import PinnedGroup from "./PinnedGroup";
import {
  ListsNavDropdown,
  NavItemLink,
  ReportsNavDropdown,
  SidebarCollapsibleGroup,
  SidebarCollapsibleTrigger,
  SidebarMenuCollapsible,
} from "./utils";

type ContentProps = {
  isGuestUser: boolean;
};

const Content = ({ isGuestUser }: ContentProps) => {
  const { logEvent } = useTracking();
  const { mutate: actionPql } = usePqlActioned(undefined, undefined, false);
  const subscription = useSubscription();
  const isRelevancySearchEnabled = useVariableValue(RELEVANCY_SEARCH, false);
  const isRelevancyNewUsersEnabled = useVariableValue(RELEVANCY_SEARCH_NEW_USERS, false);

  // Unfortunately there isn't a pure CSS way to calculate the scrollbar width, you have to do it with JS.
  // You can get close but you can't tell if a scrollbar is overlaying or taking up space.
  // If that was possible that + scrollbar-gutter: stable would allow this JS to be removed.
  const contentRef = useRef<HTMLDivElement>(null);
  const [scrollbarWidth, setScrollbarWidth] = useState<number>(11); // Being pessimistic since it's better than being optimistic

  // Can't just calculate this on first render since a scrollbar can appear/disappear based on screen height
  // and number of saved searches. Additionally MacOS switches between an overlay scrollbar and traditional
  // based on the input device by default (if using touchpad do overlay, if using mouse do traditional)
  const resizeObserver = useMemo(() => {
    return new ResizeObserver((entries) => {
      for (const entry of entries) {
        const elem = entry.target as HTMLElement;

        // Don't update if sidebar is collapsing since the animation looks bad + it's unneeded
        // and the scrollbar width is not true
        if (window.getComputedStyle(elem, null).overflowY !== "hidden") {
          setScrollbarWidth(elem.offsetWidth - elem.clientWidth);
        }
      }
    });
  }, []);
  useEffect(() => {
    if (contentRef.current) {
      // Observe element for changes
      resizeObserver.observe(contentRef.current);
      // Cleanup on unmount or when element changes
      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [resizeObserver]);

  return (
    <SidebarContentStyled
      ref={contentRef}
      // Scopes the tighter 32px row density to nav items in the scrolling content
      // area, leaving the Footer's icon-only rows at their original padding.
      data-nav-tighten="true"
      style={{ "--scrollbar-width": `${scrollbarWidth}px` } as React.CSSProperties}
    >
      {(isGuestUser || !subscription.hasDataTypes("BUYERS|SUPPLIERS")) && (
        <SidebarGroup>
          {isGuestUser ? (
            // eslint-disable-next-line no-restricted-syntax -- guest sign-up CTA to /auth/register landing (not a SPA route)
            <UpgradeButton icon="rocket02" as="a" href={routes.signUp}>
              Sign up
            </UpgradeButton>
          ) : (
            <UpgradeButton
              icon="rocket02"
              as="a"
              href={BOOK_A_CALL_LINK}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                logEvent(EventNames.paywallActioned, {
                  Action: "Book a demo in-app",
                  "Context source": "Vertical navigation",
                });
                actionPql({
                  actionType: PqlActionType.BookADemoInApp,
                  pageUrl: window.location.href,
                  contextSource: "Vertical navigation",
                });
              }}
            >
              Book a call
            </UpgradeButton>
          )}
        </SidebarGroup>
      )}

      <SidebarGroup>
        <SidebarMenu>
          <NavItemLink label="Home" href={routes.home} icon="home05" isGuestUser={isGuestUser} />
          {/* <NavItemButton label="Notifications" icon="bell01" isGuestUser={isGuestUser} /> */}
          <NavItemLink
            label="Leads"
            href={routes.leads}
            icon="target04"
            isGuestUser={isGuestUser}
          />
          <NavItemLink label="Bids" href={routes.bids} icon="feather" isGuestUser={isGuestUser} />
          <NavItemLink
            label="Content library"
            href={routes.contentLibrary}
            icon="bookClosed"
            isGuestUser={isGuestUser}
            paywall={{
              dataType: "BIDS",
              featureType: "CREATE_BIDS",
              options: { strategyPaywall: true },
            }}
          />
          <ReportsNavDropdown isGuestUser={isGuestUser} side="right" />
          <NavItemLink
            label="Settings"
            href={routes.settings}
            icon="settings01"
            isGuestUser={isGuestUser}
            onClick={() => {
              // Preserve the current page so the Settings back button returns here.
              sessionStorage.setItem("lastMainNavPageVisited", window.location.pathname);
            }}
          />
        </SidebarMenu>
      </SidebarGroup>

      <PinnedGroup isGuestUser={isGuestUser} />
      <SidebarCollapsibleGroup>
        <SidebarGroup>
          <SidebarCollapsibleTrigger>Search</SidebarCollapsibleTrigger>
          <CollapsibleContent asChild>
            <SidebarMenuCollapsible>
              <NavItemLink
                label="Notices"
                href={isRelevancyNewUsersEnabled ? routes.noticesV2 : routes.noticesEmptyState}
                activeRegex={isRelevancyNewUsersEnabled ? noticeV2ActiveRegex : noticeActiveRegex}
                icon="file02"
                isGuestUser={isGuestUser}
              />
              {isRelevancySearchEnabled && !isRelevancyNewUsersEnabled && (
                <NavItemLink
                  label="Notices 2.0"
                  href={routes.noticesV2}
                  activeRegex={noticeV2ActiveRegex}
                  icon="lightning01"
                  isGuestUser={isGuestUser}
                  tag={{ type: "beta", betaFeature: "hybridSearch" }}
                />
              )}
              <NavItemLink
                label="Documents"
                href={routes.documentsEmptyState}
                icon="box"
                isGuestUser={isGuestUser}
                paywall={{ dataType: "DOCUMENTS", featureType: "DOCUMENTS" }}
              />
              <NavItemLink
                label="Frameworks"
                href={routes.frameworks}
                icon="grid01"
                isGuestUser={isGuestUser}
                tag={{ type: "beta", betaFeature: "frameworks" }}
                paywall={{ dataType: "FRAMEWORKS", featureType: "FRAMEWORKS" }}
              />
              <NavItemLink
                label="Invoiced spend"
                href={routes.spend}
                icon="currencyPoundCircle"
                isGuestUser={isGuestUser}
                tag={{ type: "beta", betaFeature: "spendData" }}
                paywall={{ dataType: "BUYERS|SUPPLIERS", featureType: "SPEND" }}
              />
              <NavItemLink
                label="Contacts"
                href={routes.contacts}
                icon="userCircle"
                isGuestUser={isGuestUser}
                paywall={{ dataType: "CONTACTS", featureType: "CONTACTS" }}
              />
              <NavItemLink
                label="Buyers"
                href={routes.buyers}
                activeRegex={buyerActiveRegex}
                icon="wallet02"
                isGuestUser={isGuestUser}
                paywall={{ dataType: "BUYERS|SUPPLIERS", featureType: "BUYERS" }}
              />
              <NavItemLink
                label="Suppliers"
                href={routes.suppliers}
                activeRegex={supplierActiveRegex}
                icon="building07"
                isGuestUser={isGuestUser}
                paywall={{ dataType: "BUYERS|SUPPLIERS", featureType: "SUPPLIERS" }}
              />
              <NavItemLink
                label="All saved searches"
                href={routes.savedSearches}
                icon="bookmark02"
                isGuestUser={isGuestUser}
              />
              <ListsNavDropdown isGuestUser={isGuestUser} side="right" />
            </SidebarMenuCollapsible>
          </CollapsibleContent>
        </SidebarGroup>
      </SidebarCollapsibleGroup>
    </SidebarContentStyled>
  );
};

const SidebarContentStyled = styled(SidebarContent)({
  overflowX: "hidden",
  gap: 0,
  scrollbarWidth: "thin",
});

const UpgradeButton = styled(Button)({
  transition: "background-color 0.2s, border-color 0.2s, padding 0.2s ease-in-out", // both colours are the default button transition
  [`${SidebarWrapper}[data-collapsible=icon] &`]: {
    paddingRight: "4px", // Text is technically still there, need to offset gap. Could set gap to 0 but this gave a nicer transition
  },
  "& > p": {
    transition: "width 200ms ease-in-out",
    width: "84px", // Hardcoded to fit "Book a call" (fixed px needed for the collapse width animation). Slightly wide for "Sign up" but better than truncating.
    // width: "calc-size(auto, size)", // Need to have width animation with auto
  },
  [`${SidebarWrapper}[data-collapsible=icon] & > p`]: {
    width: 0,
  },
}) as unknown as CombinedButtonProps; // Styling reduces type inference so casting it back

// Regexs are exported for tests. Excludes /notices/lists and /noticesv2 so only the legacy notices section is highlighted.
export const noticeV2ActiveRegex = /^\/noticesv2/;
export const noticeActiveRegex = /^\/notices(?!\/lists)(?!v2)/;
export const buyerActiveRegex = /^\/buyers(?!\/lists)/;
export const supplierActiveRegex = /^\/suppliers/;

export default Content;
